import React from 'react';

import { AreaChartLoader } from '@rockerbox/styleguide';
import { ComposedChart, Area, Line, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { Header } from 'semantic-ui-react';

import { CustomAxisTick } from './chartHelpers';
import { Tooltip as InfoTooltip } from '../../../components';
import { roundNumber } from '../../../utils/valueFormatter';
import { titleCase } from '../helpers';

const legendStyle = { float: 'right', color: '#485CDC', fontFamily: 'Poppins' };

const InfoTooltipContent = ({ rSquared, mapeRounded }) => (
  <>
    {rSquared < 100 && (
      <p>
        <strong>
          {/* eslint-disable-next-line react/jsx-one-expression-per-line */}
          R<sup>2</sup>
        </strong>
        &nbsp;
        is the coefficient of determination. It is a measure of how well the model fits the data. The higher the value, the better.
      </p>
    )}
    {mapeRounded < 100 && (
      <p>
        <strong>MAPE</strong>
        &nbsp;
        is the mean average percentage error. It is a measure of the model&apos;s prediction accuracy. The lower the value, the better.
      </p>
    )}
  </>
);

const PredVsActualWithCI = ({ isConversionKPI, periodName, data = undefined, metrics = undefined, yMax }) => {
  if (!data || !data.length) return <AreaChartLoader style={{ marginBottom: 50 }} />;

  const chartData = data.map(row => ({
    date: row.date,
    confidence: [row.q05, row.q95],
    mean: row.q50 || row.mean,
    actual: row.actual,
  })).sort((a, b) => a.date - b.date);

  const { rsq_train, mape } = metrics || {};

  const chartFormatter = isConversionKPI
    ? new Intl.NumberFormat('en-US')
    : new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });

  const rSquared = roundNumber(rsq_train * 100);
  const mapeRounded = roundNumber(mape * 100);
  const kpiText = isConversionKPI ? 'Conversions' : 'Revenue';
  const formatting = isConversionKPI ? 'number' : 'currency';

  return (
    <>
      <Header as="h3">
        {periodName}
        {' '}
        Period
      </Header>
      <Header as="h4" style={{ margin: '1rem' }}>
        {`Predicted vs. Actual ${kpiText} (5% to 95% quantile range)`}
        <span style={legendStyle}>
          {(rSquared < 100 || mapeRounded < 100) && (
            <InfoTooltip
              position="left center"
              content={<InfoTooltipContent {...{ rSquared, mapeRounded }} />}
              style={{ fontSize: 14, marginRight: 10, marginBottom: 5 }}
            />
          )}
          {rSquared < 100 && (
            <>
              R
              <sup>2</sup>
              {` =${rSquared}%`}
            </>
          )}
          {mapeRounded < 100 && (
            <>
              &nbsp;&nbsp;&nbsp;
              {`   MAPE = ${mapeRounded}%`}
            </>
          )}
        </span>
      </Header>
      {!!chartData && !!chartData.length && (
        <div>
          <ResponsiveContainer width="100%" height={400}>
            <ComposedChart
              width={730}
              height={250}
              data={chartData}
              margin={{
                top: 20, right: 20, bottom: 40, left: 20,
              }}
            >
              <XAxis dataKey="date" angle={-45} textAnchor="end" />
              <YAxis
                type="number"
                tickLine={false}
                minTickGap={70}
                tick={CustomAxisTick('y', formatting)}
                label={{ value: kpiText, angle: -90, position: 'insideLeft', offset: 20 }}
                domain={[0, yMax]}
              />
              <Area type="linear" dataKey="confidence" stroke={null} fill="#B7ECD7" strokeWidth={3} />
              <Line type="linear" dataKey="actual" stroke="#485CDC" strokeWidth={3} dot={false} />
              <Line type="linear" dataKey="mean" stroke="#6AD9B0" strokeWidth={3} dot={false} />
              <Tooltip
                formatter={(value, name) => {
                  if (name === 'confidence') {
                    return [
                      `${chartFormatter.format(value[0])} - ${chartFormatter.format(value[1])}`,
                      '90% Confidence Interval',
                    ];
                  }
                  return [chartFormatter.format(value), titleCase(name)];
                }}
                itemSorter={item => {
                  switch (item.dataKey) {
                    case 'actual':
                      return 0;
                    case 'mean':
                      return 1;
                    case 'confidence':
                      return 2;
                    default:
                      return 3;
                  }
                }}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      )}
    </>
  );
};

export default PredVsActualWithCI;
